import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { post } from '@/lib/api';
import { useAuthMethods } from '@/lib/queries';
import { useAuthStore } from '@/stores/authStore';

const DEMO_ACCOUNTS = [
  { label: 'Super Admin', email: 'vijay.v@ikomet.com' },
  { label: 'Project Manager', email: 'pm@ikomet.test' },
  { label: 'Team Member', email: 'dev1@ikomet.test' },
  { label: 'Client', email: 'client@acme.test' },
];

/** Demo chips and prefilled credentials are development aids — hide them in production. */
const SHOW_DEMO_ACCOUNTS = import.meta.env.DEV;

/** The four-square Microsoft mark, inline so the login page fetches nothing. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, status, error } = useAuthStore();

  const [email, setEmail] = useState(SHOW_DEMO_ACCOUNTS ? 'vijay.v@ikomet.com' : '');
  const [password, setPassword] = useState(SHOW_DEMO_ACCOUNTS ? 'Passw0rd!' : '');
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { data: methods } = useAuthMethods();

  /**
   * A failed Microsoft sign-in comes back as a message on this page.
   *
   * The browser is following a redirect at that point, so the alternative is
   * landing on a raw JSON 401 with no way back.
   */
  const [params, setParams] = useSearchParams();
  const ssoError = params.get('sso_error');
  useEffect(() => {
    if (!ssoError) return;
    const next = new URLSearchParams(params);
    next.delete('sso_error');
    // Cleared from the URL so a refresh does not re-show a stale failure.
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoError]);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch {
      // The store surfaces the message; keep the form mounted so it stays visible.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
        bgcolor: 'surface.canvas',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: '#fff',
          background:
            'radial-gradient(120% 120% at 0% 0%, #3448AF 0%, #1A2557 45%, #0B1220 100%)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: '#fff',
              color: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
            }}
          >
            iK
          </Box>
          <Typography sx={{ fontWeight: 700 }}>iKomet Technology Solutions</Typography>
        </Stack>

        <Box sx={{ maxWidth: 460 }}>
          <Typography variant="h1" sx={{ fontSize: '2.5rem', lineHeight: 1.15, mb: 2 }}>
            Enterprise delivery, end to end.
          </Typography>
          <Typography sx={{ opacity: 0.8, mb: 4 }}>
            Plan portfolios, run sprints, track billable time and see delivery health across every
            client — in one platform.
          </Typography>

          <Stack spacing={1.5}>
            {[
              'Portfolio and project governance',
              'Sprint planning with velocity and burndown',
              'Billable time, approvals and utilisation',
              'Executive command center with live health',
            ].map((item) => (
              <Stack key={item} direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#7590FF',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.5 }}>
          © {new Date().getFullYear()} iKomet Technology Solutions Pvt Ltd
        </Typography>
      </Box>

      <Stack alignItems="center" justifyContent="center" sx={{ p: { xs: 3, sm: 6 } }}>
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 420, borderRadius: 3 }}
        >
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            Sign in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use your work account to continue.
          </Typography>

          {(error || ssoError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {ssoError ?? error}
            </Alert>
          )}

          {/* Above the password form, not below it: where SSO is configured it
              is the way most people are expected to sign in. */}
          {methods?.microsoft && (
            <>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                startIcon={<MicrosoftLogo />}
                onClick={() => {
                  // A full navigation, not fetch: the browser has to visit
                  // Microsoft so an existing session there can be reused.
                  window.location.href = '/api/v1/auth/sso/microsoft';
                }}
                sx={{ mb: 2 }}
              >
                Sign in with Microsoft
              </Button>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  or use your password
                </Typography>
              </Divider>
            </>
          )}

          <Stack component="form" spacing={2} onSubmit={onSubmit}>
            <TextField
              label="Work email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
              autoComplete="email"
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
              autoComplete="current-password"
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || status === 'loading'}
            >
              {submitting ? 'Please wait…' : 'Sign in'}
            </Button>

            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ alignSelf: 'center' }}
              onClick={() => setForgotOpen(true)}
            >
              Forgot your password?
            </Link>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2.5, display: 'block', textAlign: 'center' }}
          >
            Accounts are created by invitation. Ask your administrator if you need access.
          </Typography>

          {SHOW_DEMO_ACCOUNTS && (
            <>
              <Divider sx={{ my: 2.5 }}>
                <Typography variant="caption" color="text.disabled">
                  DEMO ACCOUNTS
                </Typography>
              </Divider>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {DEMO_ACCOUNTS.map((account) => (
                  <Chip
                    key={account.email}
                    label={account.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEmail(account.email);
                      setPassword('Passw0rd!');
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                    }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  mt: 1.5,
                  display: 'block',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                }}
              >
                Development build only. Seeded password: <strong>Passw0rd!</strong>
              </Typography>
            </>
          )}
        </Paper>
      </Stack>

      <ForgotPasswordDialog
        open={forgotOpen}
        defaultEmail={email}
        onClose={() => setForgotOpen(false)}
      />
    </Box>
  );
}

function ForgotPasswordDialog({
  open,
  defaultEmail,
  onClose,
}: {
  open: boolean;
  defaultEmail: string;
  onClose: () => void;
}) {
  const [address, setAddress] = useState(defaultEmail);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAddress(defaultEmail);
      setSent(false);
    }
  }, [open, defaultEmail]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await post('/auth/forgot-password', { email: address.trim() });
    } catch {
      // The endpoint deliberately never reveals whether the address exists,
      // so a failure here is still shown as success.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset your password</DialogTitle>
      <DialogContent>
        {sent ? (
          <Alert severity="success">
            If an account exists for <strong>{address}</strong>, a reset link is on its way. It
            expires in one hour.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter your work email and we'll send a link to set a new password.
            </Typography>
            <TextField
              label="Work email"
              type="email"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              autoFocus
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{sent ? 'Close' : 'Cancel'}</Button>
        {!sent && (
          <Button variant="contained" onClick={submit} disabled={!address.trim() || submitting}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
